import PropTypes from "prop-types";
import React, { useContext, useEffect, useState } from "react";

import { buttonPropTypes } from "@swa-ui/core";
import { getResponseErrorKey, isResponseOk } from "@swa-ui/fetch";
import i18n, { updateI18n } from "@swa-ui/locale";

import { AuthContext } from "../../AuthProvider";
import {
  isValidLogin,
  LOGIN_RR_MISMATCH_ERROR_CODE,
  logInvalidLoginError,
} from "../../loginValidator";
import { useUserInfo } from "../../UserInfoProvider";
import { BaseAuthenticationForm } from "../BaseAuthenticationForm";
import { AccountHeldError } from "./AccountHeldError";

/**
 * Provides a function for handling authentication for a given user interaction
 */

export const LoginForm = (props) => {
  const {
    additionalButtonProps,
    canEnroll,
    canSaveCredentials,
    formType,
    heading,
    onLoggedIn,
    stackedButtons,
    subheading,
    submitButtonText,
  } = props;
  const { handleLogin, handleLogout } = useContext(AuthContext);
  const { currentAccountNumber, currentCompanyId } = useUserInfo();
  const [errorMessage, setErrorMessage] = useState();
  const [loginFormType, setLoginFormType] = useState(formType);

  useEffect(() => {
    updateI18n({ LoginForm__ERROR_ACCOUNT_HELD: AccountHeldError });
  }, []);

  useEffect(() => {
    formType === "reAuthentication" && !currentAccountNumber
      ? setLoginFormType("login")
      : setLoginFormType(formType);
  }, [formType, currentAccountNumber]);

  return <BaseAuthenticationForm {...getProps()} />;

  function getProps() {
    const commonProps = {
      errorMessage,
      heading:
        heading ??
        i18n(
          loginFormType === "reAuthentication"
            ? "LoginForm__TITLE_LOG_IN_TO_CONTINUE"
            : "LoginForm__TITLE"
        ),
      onSubmit: handleSubmit,
      stackedButtons,
      subheading,
      submitButtonText,
    };

    const formProps = {
      login: {
        ...(stackedButtons && additionalButtonProps && { additionalButtonProps }),
        canEnroll,
        canSaveCredentials,
      },
      reAuthentication: {
        additionalButtonProps: {
          children: i18n("LoginForm__BUTTON__CANCEL"),
          styleType: "secondary",
          ...additionalButtonProps,
        },
        shouldPreventUserNameOrCompanyIdChange: true,
      },
    }[loginFormType];

    return {
      ...commonProps,
      ...formProps,
    };
  }

  async function handleSubmit(formData) {
    setErrorMessage(null);
    const companyId = formData?.companyId ? formData.companyId : currentCompanyId;
    const username = formData?.username ? formData.username : currentAccountNumber;
    const { body, responseLogin: response } = await handleLogin(formData, username, companyId);

    if (!isResponseOk(response)) {
      setErrorMessage(getResponseErrorKey(response, "LoginForm__ERROR_DEFAULT"));
    } else if (!isValidLogin(username, body, response?.headers)) {
      // Closes a potential security gap to ensure that the account being logged in with the request is in fact the account
      // being returned in the response. It mitigates potential attack vectors such as a MITM (man in the middle) or possible
      // issues in the downstream systems.
      logInvalidLoginError(username, body, response?.headers);
      setErrorMessage(
        getResponseErrorKey({ code: LOGIN_RR_MISMATCH_ERROR_CODE }, "LoginForm__ERROR_DEFAULT")
      );
      handleLogout();
    } else {
      onLoggedIn?.();
    }
  }
};

LoginForm.propTypes = {
  /** Props used for the additional optional button. */
  additionalButtonProps: PropTypes.shape(buttonPropTypes),

  /** True when 'enroll' section should be shown. */
  canEnroll: PropTypes.bool,

  /** True when 'remember me' should be shown. */
  canSaveCredentials: PropTypes.bool,

  /**
   * Which fields to display to the user. Default is login.
   * `'login'` allows creating account link and forgot password functionality.
   * `'reAuthentication'` prevents user to change username and allows cancel of the opperation
   */
  formType: PropTypes.oneOf(["login", "reAuthentication"]),

  /**
   * Triggered when cancel button is clicked.
   */
  onCancel: PropTypes.func,

  /**
   * Triggered when authentication has successfully completed.
   */
  onLoggedIn: PropTypes.func,

  /** True when buttons should be full-width and stacked vertically */
  stackedButtons: PropTypes.bool,

  /** Text to be displayed on the submit button. */
  submitButtonText: PropTypes.string,
};

LoginForm.defaultProps = {
  canEnroll: true,
  canSaveCredentials: true,
  formType: "login",
};
